import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Chip,
  CircularProgress,
  Alert,
  Link,
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  Close as CloseIcon,
  CheckCircle as CheckIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { ApiService } from '../services/api';

const OutreachPage: React.FC = () => {
  const navigate = useNavigate();
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    
    const files = Array.from(e.dataTransfer.files);
    const csvFile = files.find(file => file.type === 'text/csv' || file.name.endsWith('.csv'));
    
    if (csvFile) {
      setSelectedFile(csvFile);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFile(file);
    }
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
    setError(null);
  };

  const handleContinue = async () => {
    if (!selectedFile) return;

    try {
      setIsLoading(true);
      setError(null);

      // Upload file directly to backend - backend will parse the CSV
      const response = await ApiService.checkDomains(selectedFile);

      if (!response.sessionId) {
        throw new Error('No session ID returned from API');
      }

      // Navigate to process page with session ID
      navigate(`/outreach/process?session=${response.sessionId}`);
    } catch (err) {
      console.error('Error processing file:', err);
      let errorMessage = 'Failed to process file';
      
      if (err instanceof Error) {
        // Check if it's a network/server error
        if (err.message.includes('Failed to fetch') || 
            err.message.includes('NetworkError') ||
            err.message.includes('Network request failed') ||
            err.name === 'TypeError') {
          errorMessage = 'Server not running. Please start the server';
        } else {
          errorMessage = err.message;
        }
      }
      
      setError(errorMessage);
      setIsLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        p: 3,
        background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
      }}
    >
      {/* Header */}
      <Box sx={{ textAlign: 'center', mb: 4 }}>
        <Typography variant="h3" sx={{ fontWeight: 'bold', mb: 1 }}>
          Welcome to EMS Outreach
        </Typography>
        <Typography variant="h6" sx={{ color: 'text.secondary' }}>
          Import domain list
        </Typography>
      </Box>

      {/* Format Information Box */}
      <Alert 
        severity="info" 
        icon={<InfoIcon />}
        sx={{ 
          width: '100%', 
          maxWidth: 600, 
          mb: 3,
          '& .MuiAlert-message': {
            width: '100%'
          }
        }}
      >
        <Typography variant="body2" sx={{ mb: 1, fontWeight: 'medium' }}>
          Please upload only CSV files following the required format.
        </Typography>
        <Typography variant="body2">
          Download the template and format guide:{' '}
          <Link
            href="https://docs.google.com/spreadsheets/d/1ofa5nSIzj0zgFUhz4Tzj5bJk4WyHMx61kpEqyD-IYGA/edit?usp=sharing"
            target="_blank"
            rel="noopener noreferrer"
            sx={{ fontWeight: 'bold' }}
          >
            CSV Format Template
          </Link>
        </Typography>
      </Alert>

      {/* Upload Area */}
      <Paper
        elevation={3}
        sx={{
          width: '100%',
          maxWidth: 600,
          height: 300,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          border: isDragOver ? '3px dashed #1976d2' : '3px dashed #ccc',
          backgroundColor: isDragOver ? '#f0f8ff' : '#fafafa',
          transition: 'all 0.3s ease',
          cursor: 'pointer',
          position: 'relative',
        }}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={() => document.getElementById('file-input')?.click()}
      >
        <input
          id="file-input"
          type="file"
          accept=".csv"
          onChange={handleFileSelect}
          style={{ display: 'none' }}
        />
        
        {selectedFile ? (
          <Box sx={{ textAlign: 'center' }}>
            <CheckIcon sx={{ fontSize: 48, color: '#4caf50', mb: 2 }} />
            <Typography variant="h6" sx={{ mb: 1 }}>
              File Selected
            </Typography>
            <Chip
              label={selectedFile.name}
              color="primary"
              onDelete={handleRemoveFile}
              deleteIcon={<CloseIcon />}
            />
          </Box>
        ) : (
          <Box sx={{ textAlign: 'center' }}>
            <UploadIcon sx={{ fontSize: 64, color: '#666', mb: 2 }} />
            <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 1 }}>
              Drag & Drop .CSV here
            </Typography>
            <Typography variant="body1" sx={{ color: 'text.secondary' }}>
              or click to browse files
            </Typography>
          </Box>
        )}
      </Paper>

      {/* Error Message */}
      {error && (
        <Alert severity="error" sx={{ mt: 2, width: '100%', maxWidth: 600 }}>
          {error}
        </Alert>
      )}

      {/* Action Buttons */}
      <Box sx={{ display: 'flex', gap: 2, mt: 4, width: '100%', maxWidth: 600 }}>
        <Button
          variant="contained"
          startIcon={<UploadIcon />}
          onClick={() => document.getElementById('file-input')?.click()}
          sx={{ flex: 1 }}
          disabled={isLoading}
        >
          UPLOAD
        </Button>
        
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box
            sx={{
              width: 24,
              height: 24,
              borderRadius: '50%',
              backgroundColor: selectedFile ? '#4caf50' : '#ccc',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Typography variant="caption" sx={{ color: 'white', fontWeight: 'bold' }}>
              A
            </Typography>
          </Box>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            {selectedFile ? 'File selected' : 'No file selected'}
          </Typography>
        </Box>
        
        <Button
          variant="contained"
          onClick={handleContinue}
          disabled={!selectedFile || isLoading}
          sx={{ flex: 1 }}
          startIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : undefined}
        >
          {isLoading ? 'PROCESSING...' : 'CONTINUE'}
        </Button>
      </Box>
    </Box>
  );
};

export default OutreachPage;
